/**
 * Export payments as CSV, an HMRC-style Gift Aid CSV, or a PDF summary.
 * Exports pass the unit export gate (blocked while the unit owes credits) and are logged.
 */

import { useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, FileText, Gift, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PaymentLinkPayment } from '@masonic-tools/shared-types';
import {
  buildGiftAidCsv,
  buildPaymentsCsv,
  downloadBlob,
  downloadTextFile,
  exportFileName,
  GIFT_AID_CSV_HEADERS,
  giftAidCsvRows,
  giftAidPayments,
  PAYMENTS_CSV_HEADERS,
  paymentsCsvRows,
} from '@/lib/unit-payment-links';
import { buildPaymentsSummaryPdf } from '@/lib/payment-link-qr';
import { assertUnitExportAllowed, callableErrorInfo, logPaymentLinkExport } from '@/lib/payment-links-api';
import { showError, showSuccess } from '@/lib/swal';

type Format = 'payments_csv' | 'gift_aid_csv' | 'summary_pdf';
type Scope = 'filtered' | 'all';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  unitName: string;
  /** Title and reference for the file name and PDF heading. */
  title: string;
  reference: string;
  allPayments: PaymentLinkPayment[];
  /** Leave out when there are no filters (e.g. a unit-wide export). */
  filteredPayments?: PaymentLinkPayment[];
  linkTitles: Record<string, string>;
}

const FORMATS: Array<{ value: Format; label: string; description: string; icon: typeof FileText }> = [
  { value: 'payments_csv', label: 'Payments CSV', description: 'Every column, for spreadsheets and the treasurer', icon: FileSpreadsheet },
  { value: 'gift_aid_csv', label: 'Gift Aid CSV', description: 'HMRC schedule layout, paid Gift Aid payments only', icon: Gift },
  { value: 'summary_pdf', label: 'PDF summary', description: 'Totals, a by-day breakdown and the payment list', icon: FileText },
];

export function PaymentExportDialog({ open, onOpenChange, unitId, unitName, title, reference, allPayments, filteredPayments, linkTitles }: Props) {
  const [format, setFormat] = useState<Format>('payments_csv');
  const [scope, setScope] = useState<Scope>(filteredPayments ? 'filtered' : 'all');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setScope(filteredPayments ? 'filtered' : 'all');
  }, [open, filteredPayments]);

  const source = scope === 'filtered' && filteredPayments ? filteredPayments : allPayments;
  const rowsForFormat = useMemo(() => (format === 'gift_aid_csv' ? giftAidPayments(source) : format === 'summary_pdf' ? source.filter((p) => p.status === 'paid') : source), [format, source]);
  const previewRows = useMemo(() => {
    if (format === 'gift_aid_csv') return { headers: GIFT_AID_CSV_HEADERS, rows: giftAidCsvRows(source).slice(0, 3) };
    return { headers: PAYMENTS_CSV_HEADERS.slice(0, 9), rows: paymentsCsvRows(source.slice(0, 3), linkTitles).map((r) => r.slice(0, 9)) };
  }, [format, source, linkTitles]);

  const handleDownload = async () => {
    setBusy(true);
    try {
      try {
        await assertUnitExportAllowed({ unitId });
      } catch (e) {
        const info = callableErrorInfo(e, 'Exports are not available right now');
        showError(info.message === 'CREDIT_DEBT' ? 'Exports are paused while the unit has a negative credit balance. Buy credits to continue.' : info.message);
        return;
      }
      const now = new Date();
      if (format === 'payments_csv') {
        downloadTextFile(buildPaymentsCsv(source, linkTitles), exportFileName(reference, 'payments', now, 'csv'));
      } else if (format === 'gift_aid_csv') {
        downloadTextFile(buildGiftAidCsv(source), exportFileName(reference, 'gift-aid', now, 'csv'));
      } else {
        const blob = await buildPaymentsSummaryPdf({ unitName, title, reference, payments: source, linkTitles });
        downloadBlob(blob, exportFileName(reference, 'summary', now, 'pdf'));
      }
      const linkIds = Array.from(new Set(source.map((p) => p.linkId)));
      logPaymentLinkExport({ unitId, linkIds, format, count: rowsForFormat.length }).catch(() => undefined);
      showSuccess('Export downloaded');
      onOpenChange(false);
    } catch {
      showError('Could not create the export');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Export payments</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          {filteredPayments ? (
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Payments to include</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {(
                  [
                    ['filtered', `Current filters (${filteredPayments.length})`],
                    ['all', `All payments (${allPayments.length})`],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className={cn('flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm', scope === value ? 'border-primary bg-primary/5' : 'border-border')}>
                    <input type="radio" name="pl-export-scope" checked={scope === value} onChange={() => setScope(value)} className="accent-[hsl(var(--primary))]" />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          <fieldset>
            <legend className="mb-2 text-sm font-medium">Format</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {FORMATS.map(({ value, label, description, icon: Icon }) => (
                <label key={value} className={cn('flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-sm', format === value ? 'border-primary bg-primary/5' : 'border-border')}>
                  <span className="flex items-center gap-2 font-medium">
                    <input type="radio" name="pl-export-format" checked={format === value} onChange={() => setFormat(value)} className="accent-[hsl(var(--primary))]" />
                    <Icon className="h-4 w-4" /> {label}
                  </span>
                  <span className="text-xs text-muted-foreground">{description}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <p className="mb-2 text-sm font-medium">
              Preview <span className="font-normal text-muted-foreground">· {rowsForFormat.length} row{rowsForFormat.length === 1 ? '' : 's'}</span>
            </p>
            {format === 'summary_pdf' ? (
              <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">A PDF with totals, a by-day breakdown and {rowsForFormat.length} paid payments.</p>
            ) : previewRows.rows.length === 0 ? (
              <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                {format === 'gift_aid_csv' ? 'No paid payments with Gift Aid to export.' : 'No payments to export.'}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      {previewRows.headers.map((h) => (
                        <th key={h} className="whitespace-nowrap px-2 py-1 text-left font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        {r.map((c, j) => (
                          <td key={j} className="whitespace-nowrap px-2 py-1">
                            {String(c ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={busy || rowsForFormat.length === 0}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
